/*
 * RIDECLOUD RATE CALCULATOR
 * Vehicles are fetched live from Zoho CRM via /api/vehicles proxy endpoint.
 * Makes and models are derived from the live catalogue — no hardcoded data.
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Calculator, Loader2 } from "lucide-react";

interface Vehicle {
  id: string | number;
  make: string;
  model: string;
  variant: string;
  fromPrice: number;
  rate6: number;
  rate12: number;
  rate24: number;
  km: number;
}

const termMultipliers: Record<string, number> = {
  "12": 1.06,
  "24": 1.0,
  "36": 0.95,
};

const additionalDriverFee = 299;
const deliveryFee = 1500;
const km2000Surcharge = 350;  // extra per month for 2,000 km pack
const km2500Surcharge = 700;  // extra per month for 2,500 km pack

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.6rem 0.875rem",
  border: "1px solid rgba(46,0,79,0.2)",
  borderRadius: "0.375rem",
  fontSize: "0.875rem",
  color: "#2e004f",
  backgroundColor: "#ffffff",
  outline: "none",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "0.8rem",
  fontWeight: 600,
  color: "#2e004f",
  marginBottom: "0.375rem",
};

export default function RateCalculator() {
  const [, navigate] = useLocation();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);

  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [term, setTerm] = useState("36");
  const [km, setKm] = useState<"1500" | "2000" | "2500">("1500");
  const [additionalDrivers, setAdditionalDrivers] = useState(0);
  const [delivery, setDelivery] = useState(false);

  useEffect(() => {
    fetch("/api/vehicles")
      .then((r) => r.json())
      .then((data) => {
        setVehicles(data.vehicles || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Derive unique makes from live data
  const makes = Array.from(new Set(vehicles.map((v) => v.make))).sort();

  // Derive models for selected make
  const modelsForMake = vehicles.filter((v) => v.make === make);

  const handleMakeChange = (newMake: string) => {
    setMake(newMake);
    setModel("");
    setSelectedVehicle(null);
  };

  const handleModelChange = (modelVariant: string) => {
    setModel(modelVariant);
    const found = vehicles.find((v) => v.make === make && `${v.model} ${v.variant}`.trim() === modelVariant);
    setSelectedVehicle(found || null);
  };

  // Calculate base price from live Zoho rates
  const getBasePrice = (): number => {
    if (!selectedVehicle) return 0;
    const termMultiplier = termMultipliers[term] || 1;
    // Use the closest available rate from Zoho, then apply term multiplier
    const rate24 = selectedVehicle.rate24 || selectedVehicle.fromPrice;
    const base = Math.round(rate24 * termMultiplier);
    const kmExtra = km === "2500" ? km2500Surcharge : km === "2000" ? km2000Surcharge : 0;
    return base + kmExtra;
  };

  const basePrice = getBasePrice();
  const monthlyExtras = additionalDrivers * additionalDriverFee;
  const onceOff = delivery ? deliveryFee : 0;
  const monthlyTotal = basePrice > 0 ? basePrice + monthlyExtras : 0;

  return (
    <section id="calculator" className="py-16 md:py-20" style={{ backgroundColor: "#f8f7fa" }}>
      <div className="container">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-semibold mb-2" style={{ color: "#2e004f" }}>
            Rate Calculator
          </h2>
          <p className="text-base" style={{ color: "#5a3d70" }}>
            Select a vehicle, term and extras to estimate your all-inclusive monthly cost.
          </p>
        </div>

        {/* Calculator card */}
        <div
          className="rounded-lg overflow-hidden"
          style={{ border: "1px solid rgba(46,0,79,0.12)", backgroundColor: "#ffffff" }}
        >
          <div className="grid lg:grid-cols-2">
            {/* Left: Inputs */}
            <div className="p-6 md:p-8 space-y-5" style={{ borderRight: "1px solid rgba(46,0,79,0.08)" }}>

              {loading ? (
                <div className="flex items-center gap-2 py-4" style={{ color: "#7a6a8a" }}>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading vehicles from catalogue…</span>
                </div>
              ) : (
                <>
                  {/* Make */}
                  <div>
                    <label style={labelStyle}>Vehicle Make</label>
                    <select
                      value={make}
                      onChange={(e) => handleMakeChange(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Select Make…</option>
                      {makes.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>

                  {/* Model */}
                  <div>
                    <label style={labelStyle}>Vehicle Model</label>
                    <select
                      value={model}
                      onChange={(e) => handleModelChange(e.target.value)}
                      disabled={!make}
                      style={{ ...selectStyle, opacity: !make ? 0.5 : 1 }}
                    >
                      <option value="">Select Model…</option>
                      {modelsForMake.map((v) => {
                        const label = `${v.model} ${v.variant}`.trim();
                        return <option key={v.id} value={label}>{label}</option>;
                      })}
                    </select>
                  </div>
                </>
              )}

              {/* Term */}
              <div>
                <label style={labelStyle}>Subscription Term</label>
                <div className="flex gap-2 flex-wrap">
                  {["12", "24", "36"].map((t) => (
                    <button
                      key={t}
                      onClick={() => setTerm(t)}
                      className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                      style={
                        term === t
                          ? { backgroundColor: "#e6531e", color: "#ffffff", border: "1px solid #e6531e" }
                          : { backgroundColor: "transparent", color: "#2e004f", border: "1px solid rgba(46,0,79,0.25)" }
                      }
                    >
                      {t} months
                    </button>
                  ))}
                </div>
              </div>

              {/* KM Pack */}
              <div>
                <label style={labelStyle}>Monthly KM Pack</label>
                <div className="flex gap-3">
                  {(["1500", "2000", "2500"] as const).map((k) => (
                    <button
                      key={k}
                      onClick={() => setKm(k)}
                      className="flex-1 py-2.5 rounded-full text-sm font-medium transition-all"
                      style={
                        km === k
                          ? { backgroundColor: "#e6531e", color: "#ffffff", border: "1px solid #e6531e" }
                          : { backgroundColor: "transparent", color: "#2e004f", border: "1px solid rgba(46,0,79,0.25)" }
                      }
                    >
                      {parseInt(k).toLocaleString()} km
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Drivers */}
              <div>
                <label style={labelStyle}>Additional Drivers (R{additionalDriverFee}/driver/month)</label>
                <div
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ border: "1px solid rgba(46,0,79,0.15)", backgroundColor: "#f8f7fa" }}
                >
                  <span className="text-sm" style={{ color: "#2e004f" }}>Number of additional drivers</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setAdditionalDrivers(Math.max(0, additionalDrivers - 1))}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold transition-colors"
                      style={{ backgroundColor: "rgba(46,0,79,0.08)", color: "#2e004f" }}
                    >
                      −
                    </button>
                    <span className="text-base font-bold w-5 text-center" style={{ color: "#2e004f" }}>{additionalDrivers}</span>
                    <button
                      onClick={() => setAdditionalDrivers(Math.min(5, additionalDrivers + 1))}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold transition-colors"
                      style={{ backgroundColor: "rgba(46,0,79,0.08)", color: "#2e004f" }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Delivery toggle */}
              <div>
                <label style={labelStyle}>Doorstep Delivery (once-off R{deliveryFee.toLocaleString()})</label>
                <div
                  className="flex items-center justify-between p-3 rounded-lg cursor-pointer"
                  style={{ border: "1px solid rgba(46,0,79,0.15)", backgroundColor: "#f8f7fa" }}
                  onClick={() => setDelivery(!delivery)}
                >
                  <span className="text-sm" style={{ color: "#2e004f" }}>Add doorstep delivery</span>
                  <div
                    className="w-11 h-6 rounded-full relative transition-all duration-200"
                    style={{ backgroundColor: delivery ? "#e6531e" : "rgba(46,0,79,0.15)" }}
                  >
                    <div
                      className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-200"
                      style={{ left: delivery ? "1.5rem" : "0.25rem" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Price Display */}
            <div className="p-6 md:p-8 flex flex-col justify-center" style={{ backgroundColor: "#f8f7fa" }}>
              <div
                className="rounded-lg p-6 text-center"
                style={{ border: "1px solid rgba(46,0,79,0.12)", backgroundColor: "#ffffff" }}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: "rgba(230,83,30,0.1)" }}
                >
                  <Calculator className="w-7 h-7" style={{ color: "#e6531e" }} />
                </div>

                {monthlyTotal > 0 ? (
                  <>
                    <p className="text-sm mb-1" style={{ color: "#7a6a8a" }}>Estimated Monthly Rate</p>
                    <p className="text-5xl font-bold mb-1" style={{ color: "#2e004f" }}>
                      R{monthlyTotal.toLocaleString()}
                    </p>
                    <p className="text-sm mb-5" style={{ color: "#9a8aaa" }}>per month</p>

                    {onceOff > 0 && (
                      <div
                        className="mb-5 px-4 py-2 rounded-full text-sm inline-block"
                        style={{ backgroundColor: "rgba(230,83,30,0.08)", color: "#e6531e", border: "1px solid rgba(230,83,30,0.2)" }}
                      >
                        + R{onceOff.toLocaleString()} once-off delivery
                      </div>
                    )}

                    {/* Breakdown */}
                    <div className="text-left space-y-2 mb-5">
                      <div className="flex justify-between text-sm">
                        <span style={{ color: "#7a6a8a" }}>Base subscription</span>
                        <span className="font-medium" style={{ color: "#2e004f" }}>R{basePrice.toLocaleString()}</span>
                      </div>
                      {additionalDrivers > 0 && (
                        <div className="flex justify-between text-sm">
                          <span style={{ color: "#7a6a8a" }}>{additionalDrivers} additional driver{additionalDrivers > 1 ? "s" : ""}</span>
                          <span className="font-medium" style={{ color: "#2e004f" }}>R{monthlyExtras.toLocaleString()}</span>
                        </div>
                      )}
                      <div
                        className="flex justify-between text-sm font-bold pt-2"
                        style={{ borderTop: "1px solid rgba(46,0,79,0.1)" }}
                      >
                        <span style={{ color: "#2e004f" }}>Monthly Total</span>
                        <span style={{ color: "#e6531e" }}>R{monthlyTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => selectedVehicle ? navigate(`/vehicle/${selectedVehicle.id}`) : undefined}
                      className="btn-orange w-full justify-center"
                    >
                      View Full Details &amp; Apply
                    </button>
                  </>
                ) : (
                  <div className="py-4">
                    <p className="text-base mb-1" style={{ color: "#7a6a8a" }}>Select a vehicle and term</p>
                    <p className="text-sm" style={{ color: "#b0a0c0" }}>to see your estimated monthly rate</p>
                  </div>
                )}
              </div>

              <p className="text-xs text-center mt-4" style={{ color: "#b0a0c0" }}>
                Estimates are indicative. Final rates subject to credit approval and affordability assessment.
              </p>
              <p className="text-xs text-center mt-2" style={{ color: "#b0a0c0" }}>
                Once-off charges (initiation fee &amp; security deposit) apply — contact us for details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
